import { Refresh } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslate } from "react-admin";
import { useLocation } from "react-router-dom";

import {
  LearningProgressEntry,
  LearningProgressPathAssignment,
  LearningProgressStatus,
  useLearningProgress,
} from "../../hooks/useLearningProgress";
import { borderRadius, color, space } from "../../theme/designTokens";
import {
  formatLearningPercent,
  LearningProgressSummaryTiles,
  LearningProgressTable,
  LearningStatusChip,
} from "./LearningProgressTable";

type Pivot = "course" | "caregiver";

type PathGroup = {
  key: string;
  title: string;
  enrollmentIds: string[];
  entries: LearningProgressEntry[];
};

const pathStatus = (entries: LearningProgressEntry[], total: number): LearningProgressStatus => {
  if (total > 0 && entries.filter(entry => entry.status === "COMPLETED").length === total) return "COMPLETED";
  if (entries.some(entry => entry.status !== "NOT_STARTED")) return "IN_PROGRESS";
  return "NOT_STARTED";
};

const pathGroupsFor = (
  userId: string,
  assignments: LearningProgressPathAssignment[],
  entries: LearningProgressEntry[],
  paths: Array<{ id: string; version: string; title: string }>,
  unavailableTitle: string
): PathGroup[] => {
  const entriesById = new Map(entries.map(entry => [entry.enrollmentId, entry]));
  const groups = new Map<string, { title: string; enrollmentIds: Set<string> }>();
  assignments
    .filter(assignment => assignment.userId === userId)
    .forEach(assignment => {
      const key = `${assignment.learningPathId}:${assignment.learningPathVersion ?? "unknown"}`;
      const path =
        paths.find(item => item.id === assignment.learningPathId && item.version === assignment.learningPathVersion) ??
        paths.find(item => item.id === assignment.learningPathId);
      const group = groups.get(key) ?? { title: path?.title ?? unavailableTitle, enrollmentIds: new Set<string>() };
      assignment.enrollmentIds.forEach(enrollmentId => group.enrollmentIds.add(enrollmentId));
      groups.set(key, group);
    });
  return Array.from(groups, ([key, group]) => {
    const enrollmentIds = Array.from(group.enrollmentIds);
    return {
      key,
      title: group.title,
      enrollmentIds,
      entries: enrollmentIds.flatMap(enrollmentId => {
        const entry = entriesById.get(enrollmentId);
        return entry ? [entry] : [];
      }),
    };
  }).sort((left, right) => left.title.localeCompare(right.title, "de-DE"));
};

const EmptyReport = ({ message }: { message: string }) => (
  <Paper variant="outlined" sx={{ p: space.lg, textAlign: "center", borderColor: color.panelBorder }}>
    <Typography color="text.secondary">{message}</Typography>
  </Paper>
);

export const LearningProgressList = () => {
  const translate = useTranslate();
  const { search } = useLocation();
  const { caregivers, courses, learningPaths, pathAssignments, entries, loading, error, reload } =
    useLearningProgress();
  const initialCaregiverId = useMemo(() => new URLSearchParams(search).get("caregiver") ?? "", [search]);
  const [pivot, setPivot] = useState<Pivot>(initialCaregiverId ? "caregiver" : "course");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(initialCaregiverId);
  const coursesById = useMemo(() => new Map(courses.map(course => [course.id, course])), [courses]);
  const courseOptions = useMemo(
    () =>
      Array.from(new Set(entries.map(entry => entry.courseId)))
        .map(courseId => ({
          id: courseId,
          title: coursesById.get(courseId)?.title ?? translate("learningProgress.unavailableCourse"),
        }))
        .sort((left, right) => left.title.localeCompare(right.title, "de-DE")),
    [coursesById, entries, translate]
  );
  const caregiverOptions = useMemo(
    () => [...caregivers].sort((left, right) => left.displayName.localeCompare(right.displayName, "de-DE")),
    [caregivers]
  );

  const activeCourseId = courseOptions.some(course => course.id === selectedCourseId)
    ? selectedCourseId
    : (courseOptions[0]?.id ?? "");
  const activeUserId = caregiverOptions.some(caregiver => caregiver.userId === selectedUserId)
    ? selectedUserId
    : (caregiverOptions[0]?.userId ?? "");

  if (loading) return <CircularProgress aria-label={translate("learningProgress.loading")} />;
  if (error) {
    return (
      <Alert
        severity="error"
        action={<Button onClick={() => void reload()}>{translate("learningProgress.actions.retry")}</Button>}
      >
        {translate("learningProgress.loadError")}
      </Alert>
    );
  }

  const selectedEntries = entries.filter(entry =>
    pivot === "course" ? entry.courseId === activeCourseId : entry.userId === activeUserId
  );
  const pathGroups = pathGroupsFor(
    activeUserId,
    pathAssignments,
    selectedEntries,
    learningPaths,
    translate("learningProgress.unavailablePath")
  );
  const groupedEnrollmentIds = new Set(pathGroups.flatMap(group => group.enrollmentIds));
  const ungroupedEntries = selectedEntries.filter(entry => !groupedEnrollmentIds.has(entry.enrollmentId));

  return (
    <Box data-testid="learning-progress-report">
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: space.sm, flexWrap: "wrap" }}>
        <Box>
          <Typography component="h1" variant="h4">
            {translate("learningProgress.title")}
          </Typography>
          <Typography color="text.secondary">{translate("learningProgress.description")}</Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={() => void reload()}>
          {translate("learningProgress.actions.refresh")}
        </Button>
      </Box>

      <Tabs
        value={pivot}
        onChange={(_event, value: Pivot) => setPivot(value)}
        aria-label={translate("learningProgress.pivots.label")}
      >
        <Tab value="course" label={translate("learningProgress.pivots.byCourse")} />
        <Tab value="caregiver" label={translate("learningProgress.pivots.byCaregiver")} />
      </Tabs>

      <Stack gap={space.sm} mt={space.sm}>
        <TextField
          select
          size="small"
          label={translate(pivot === "course" ? "learningProgress.selectCourse" : "learningProgress.selectCaregiver")}
          value={pivot === "course" ? activeCourseId : activeUserId}
          onChange={event =>
            pivot === "course" ? setSelectedCourseId(event.target.value) : setSelectedUserId(event.target.value)
          }
          sx={{ maxWidth: 420 }}
        >
          {(pivot === "course" ? courseOptions : caregiverOptions).map(option => {
            const id = "id" in option ? option.id : option.userId;
            const label = "title" in option ? option.title : option.displayName;
            return (
              <MenuItem key={id} value={id}>
                {label}
              </MenuItem>
            );
          })}
        </TextField>
        <LearningProgressSummaryTiles entries={selectedEntries} />

        {selectedEntries.length === 0 ? (
          <EmptyReport message={translate("learningProgress.empty")} />
        ) : pivot === "course" ? (
          <Paper variant="outlined" sx={{ borderRadius: borderRadius.md, borderColor: color.panelBorder }}>
            <LearningProgressTable
              entries={selectedEntries}
              caregivers={caregivers}
              courses={courses}
              primaryColumn="caregiver"
            />
          </Paper>
        ) : (
          <Stack gap={space.sm}>
            {pathGroups.map(group => {
              const completed = group.entries.filter(entry => entry.status === "COMPLETED").length;
              const percentage = group.enrollmentIds.length === 0 ? 0 : (completed / group.enrollmentIds.length) * 100;
              return (
                <Paper key={group.key} variant="outlined" sx={{ p: space.sm, borderColor: color.panelBorder }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: space.sm, flexWrap: "wrap" }}>
                    <Typography component="h2" variant="h6">
                      {group.title}
                    </Typography>
                    <LearningStatusChip status={pathStatus(group.entries, group.enrollmentIds.length)} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {translate("learningProgress.pathCompleted", { completed, total: group.enrollmentIds.length })} ·{" "}
                    {formatLearningPercent(percentage)}
                  </Typography>
                  <LinearProgress variant="determinate" value={percentage} sx={{ my: space.xs }} />
                  <LearningProgressTable
                    entries={group.entries}
                    caregivers={caregivers}
                    courses={courses}
                    primaryColumn="course"
                  />
                </Paper>
              );
            })}
            {ungroupedEntries.length > 0 && (
              <Paper variant="outlined" sx={{ p: space.sm, borderColor: color.panelBorder }}>
                {pathGroups.length > 0 && (
                  <Typography component="h2" variant="h6">
                    {translate("learningProgress.otherCourses")}
                  </Typography>
                )}
                <LearningProgressTable
                  entries={ungroupedEntries}
                  caregivers={caregivers}
                  courses={courses}
                  primaryColumn="course"
                />
              </Paper>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
